import { Component, inject, signal, computed, OnDestroy, OnInit, effect } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { rxResourceFixed } from '@zayan-erp/shared';
import {
  AbilityService,
  HeaderService,
  LookupsService,
  GenericDropdownComponent,
  DropdownData
} from '@zayan-erp/shared';
import { BuildSpecModelsTableComponent } from './build-spec-models-table/build-spec-models-table.component';
import { BuildSpecModelsDetailsComponent } from './build-spec-models-details/build-spec-models-details.component';
import { of, startWith, Subject, debounceTime, distinctUntilChanged, map } from 'rxjs';
import { SpecLot } from '../../models/build-spec-models.models';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { SalesService } from '../../services/sales.service';
import { SalesOfficeService } from '@zayan-erp/home-building';

@Component({
  selector: 'app-sales-build-spec-models',
  imports: [
    BuildSpecModelsTableComponent,
    BuildSpecModelsDetailsComponent,
    GenericDropdownComponent,
    ReactiveFormsModule
  ],
  templateUrl: './build-spec-models.component.html'
})
export class SalesBuildSpecModelsComponent implements OnInit, OnDestroy {
  readonly #abilityService = inject(AbilityService);
  readonly #headerSearchService = inject(HeaderService);
  readonly #lookupsService = inject(LookupsService);
  readonly #salesService = inject(SalesService);
  readonly #salesOfficeService = inject(SalesOfficeService);
  readonly #router = inject(Router);
  readonly #route = inject(ActivatedRoute);

  readonly ability = this.#abilityService.ability;
  readonly showDetails = signal<boolean>(false);
  readonly selectedSpecLot = signal<SpecLot | null>(null);

  readonly #searchQuerySubject = new Subject<string>();
  readonly searchQuery = toSignal(this.#searchQuerySubject.pipe(debounceTime(300), distinctUntilChanged()), {
    initialValue: ''
  });

  readonly projectControl = new FormControl<string | null>(this.#route.snapshot.queryParamMap.get('projectId'));

  readonly projectIdFromUrl = toSignal(this.#route.queryParamMap.pipe(map(params => params.get('projectId'))), {
    initialValue: null
  });

  readonly specLotIdFromUrl = toSignal(this.#route.queryParamMap.pipe(map(params => params.get('specLotId'))), {
    initialValue: null
  });

  readonly selectedSalesOfficeId = toSignal(this.#salesService.saleOfficeId$, { initialValue: null as string | null });

  readonly projectsResource = rxResourceFixed({
    params: () => ({ salesOfficeId: this.selectedSalesOfficeId() }),
    defaultValue: [],
    stream: ({ params }) => {
      if (!params.salesOfficeId) return of([]);
      return this.#salesOfficeService.getSalesOfficeProjects(params.salesOfficeId);
    }
  });

  readonly projectsDropdownData = computed<DropdownData[]>(() =>
    (this.projectsResource.value() ?? []).map(p => ({
      id: p.projectId ?? (p as any).id,
      firstLabel: p.projectCode ?? p.projectName ?? '',
      secondLabel: p.projectName ?? ''
    }))
  );

  readonly selectedProjectIdFromControl = toSignal(
    this.projectControl.valueChanges.pipe(startWith(this.projectControl.value)),
    {
      initialValue: null
    }
  );

  readonly selectedProjectId = computed(() => this.selectedProjectIdFromControl() ?? this.projectIdFromUrl());

  readonly specLotsResource = rxResourceFixed({
    params: () => ({ projectId: this.selectedProjectId() }),
    defaultValue: [],
    stream: ({ params }) => (params.projectId ? this.#salesService.readSpecLots(params.projectId) : of([]))
  });

  readonly allSpecLots = computed(() => this.specLotsResource.value() ?? []);

  readonly specLots = computed(() => {
    const lots = this.allSpecLots();
    const query = this.searchQuery().toLowerCase().trim();
    return query ? this.#filterSpecLots(lots, query) : lots;
  });

  readonly showNoResultsMessage = computed(() => {
    return (
      this.hasProjectSelected() && !!this.searchQuery() && this.allSpecLots().length > 0 && this.specLots().length === 0
    );
  });

  readonly hasProjectSelected = computed(() => !!this.selectedProjectId());
  readonly hasSpecLots = computed(() => this.hasProjectSelected() && this.specLots().length > 0);

  constructor() {
    effect(() => {
      const urlProjectId = this.projectIdFromUrl();
      if (urlProjectId && urlProjectId !== this.projectControl.value) {
        this.projectControl.setValue(urlProjectId, { emitEvent: false });
      }
    });

    this.projectControl.valueChanges.pipe(takeUntilDestroyed()).subscribe(projectId => {
      const urlProjectId = this.projectIdFromUrl();
      if (projectId !== urlProjectId) {
        this.#updateUrl({
          projectId: projectId || null,
          specLotId: projectId ? this.specLotIdFromUrl() : null
        });
      }
    });

    effect(() => {
      const lots = this.allSpecLots();
      const specLotId = this.specLotIdFromUrl();

      if (!lots.length) return;

      if (specLotId) {
        const specLot = lots.find(lot => lot.id === specLotId);
        if (specLot) {
          this.selectedSpecLot.set(specLot);
          this.showDetails.set(true);
        }
      } else {
        const wasShowingDetails = this.showDetails();
        this.selectedSpecLot.set(null);
        this.showDetails.set(false);
        if (wasShowingDetails && this.selectedProjectId()) {
          this.specLotsResource.reload();
        }
      }
    });
  }

  ngOnInit(): void {
    this.#headerSearchService.setSearchConfig({
      showSearch: false,
      showIcon: false,
      placeholder: 'Search Build Spec & Models',
      showSearchResults: false,
      search: (query: string) => {
        this.#searchQuerySubject.next(query);
        return of([]);
      },
      handleResult: () => {}
    });
  }

  readonly #filterSpecLots = (lots: SpecLot[], query: string): SpecLot[] => {
    return lots.filter(lot => {
      const searchFields = [
        lot.lotNo,
        lot.lotTypeName,
        lot.status,
        lot.legalUnit,
        lot.address && [lot.address.streetNo, lot.address.street, lot.address.street2].filter(Boolean).join(' '),
        lot.selectedModel?.modelName,
        lot.models?.[0]?.modelName
      ];
      return searchFields.some(field => field?.toLowerCase().includes(query));
    });
  };

  #updateUrl(params: { projectId?: string | null; specLotId?: string | null }): void {
    const queryParams = { ...this.#route.snapshot.queryParams };

    if (params.projectId !== undefined) queryParams['projectId'] = params.projectId || undefined;
    if (params.specLotId !== undefined) queryParams['specLotId'] = params.specLotId || undefined;

    Object.keys(queryParams).forEach(key => {
      if (!queryParams[key]) delete queryParams[key];
    });

    this.#router.navigate([], { relativeTo: this.#route, queryParams });
  }

  onRowClick(specLot: SpecLot): void {
    this.selectedSpecLot.set(specLot);
    this.showDetails.set(true);
    this.#updateUrl({ projectId: this.selectedProjectId(), specLotId: specLot.id });
  }

  closeDetails(): void {
    this.showDetails.set(false);
    this.selectedSpecLot.set(null);
    this.#updateUrl({ projectId: this.selectedProjectId(), specLotId: null });
    this.specLotsResource.reload();
  }
  ngOnDestroy(): void {
    this.#headerSearchService.clearSearchConfig();
  }
}
